import { createTokenStorage, SecureTokenStorage } from './tokenStorage';
import { config } from './config';

export interface FeedbackSubmissionData {
  productId: string;
  rating?: number | null;
  type: 'bug' | 'feedback';
  comment: string;
  pageUrl: string;
  email: string;
  screenshot?: string;    // Base64 encoded image
  harFile?: string;       // HAR file as string
  consoleLogs?: any[];
  networkRequests?: any[];
  browserInfo?: {
    userAgent: string;
    viewportWidth: number;
    viewportHeight: number;
    url: string;
  };
  selectedElement?: {
    selector: string;
    textContent?: string;
  };
}

export interface FeedbackSubmissionResponse {
  success: boolean;
  id: string;
  submittedAt: string;
}

export interface FeedbackApiResponse {
  feedback: FeedbackEntity[];
  user: {
    email: string;
    isAdmin: boolean;
    allowedProducts: string[];
  };
  filters?: {
    productId?: string;
    timeframe?: number;
    type?: string;
    rating?: number;
  };
}

export interface FeedbackEntity {
  partitionKey: string;     // productId
  rowKey: string;          // timestamp-feedbackId
  id: string;              // Unique feedback ID
  submittedAt: string;     // ISO timestamp
  rating: number | null;   // 1-5 stars or null
  type: 'bug' | 'feedback';
  comment: string;         // User feedback text
  pageUrl: string;         // Page where submitted
  email: string;           // Reporter email
  attachmentUrls: string;  // JSON array of blob URLs
  metadata: string;        // JSON of browser info, etc.
}

export interface ProductsApiResponse {
  products: string[];
  user: {
    email: string;
    isAdmin: boolean;
    allowedProducts: string[];
  };
}

export interface FeedbackFilters {
  productId?: string;
  timeframe?: number;
  type?: 'bug' | 'feedback';
  rating?: number;
}

class FeedbackApiClient {
  private baseUrl: string;
  private getAuthToken: () => Promise<string | null>;

  constructor(baseUrl: string, getAuthToken: () => Promise<string | null>) {
    this.baseUrl = baseUrl.replace(/\/$/, ''); // Remove trailing slash
    this.getAuthToken = getAuthToken;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = await this.getAuthToken();
    
    if (!token) {
      throw new Error('No authentication token available');
    }

    const url = `${this.baseUrl}${endpoint}`;
    
    const requestOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Access-Token': `Bearer ${token}`,  // Custom header to bypass Azure SWA interference
        ...options.headers,
      },
    };

    const response = await fetch(url, requestOptions);    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorData = {};
      
      // Clone the response so we can read it multiple times if needed
      const responseClone = response.clone();
      
      try {
        // Try to read as JSON first
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
          errorMessage = (errorData as any).message || errorMessage;
        } else {
          // Not JSON, try to read as text
          const errorText = await response.text();
          if (errorText.trim()) {
            errorMessage += ` - ${errorText}`;
          }
        }
      } catch (readError) {
        // Try the cloned response as text if JSON parsing failed
        try {
          const fallbackText = await responseClone.text();
          if (fallbackText.trim()) {
            errorMessage += ` - ${fallbackText}`;
          }
        } catch (fallbackError) {
          // Response body is likely empty or corrupted
          errorMessage += ' (Empty response body)';
        }
      }
      
      throw new Error(errorMessage);
    }

    const responseData = await response.json() as Promise<T>;
    return responseData;
  }

  async getFeedback(filters: FeedbackFilters = {}): Promise<FeedbackApiResponse> {
    const queryParams = new URLSearchParams();
    
    if (filters.productId) queryParams.set('productId', filters.productId);
    if (filters.timeframe) queryParams.set('timeframe', filters.timeframe.toString());
    if (filters.type) queryParams.set('type', filters.type);
    if (filters.rating !== undefined) queryParams.set('rating', filters.rating.toString());

    const endpoint = `/feedback${queryParams.toString() ? `?${queryParams}` : ''}`;
    return this.makeRequest<FeedbackApiResponse>(endpoint);
  }

  async getFeedbackById(id: string, productId?: string): Promise<{ feedback: FeedbackEntity; user: any }> {
    const queryParams = new URLSearchParams();
    if (productId) queryParams.set('productId', productId);
    
    const endpoint = `/feedback/${id}${queryParams.toString() ? `?${queryParams}` : ''}`;
    return this.makeRequest<{ feedback: FeedbackEntity; user: any }>(endpoint);
  }

  async getProducts(): Promise<ProductsApiResponse> {
    return this.makeRequest<ProductsApiResponse>('/products');
  }

  async submitFeedback(data: FeedbackSubmissionData): Promise<FeedbackSubmissionResponse> {
    return this.makeRequest<FeedbackSubmissionResponse>('/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Test API connectivity and authentication
   */
  async testConnection(): Promise<{ status: string; message: string; details?: any }> {
    try {
      // Try a simple health check first (without auth) to test basic connectivity
      try {
        const healthResponse = await fetch(`${this.baseUrl}/health`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (healthResponse.ok) {
          await healthResponse.text();
        }
      } catch (healthError) {
        return {
          status: 'error',
          message: 'Basic connectivity test failed',
          details: { healthError: String(healthError) }
        };
      }
      
      // Test authentication
      const token = await this.getAuthToken();
      if (!token) {
        return {
          status: 'error',
          message: 'No authentication token available',
          details: { tokenAvailable: false }
        };
      }
      
      // Test authenticated endpoint (try products endpoint as it might be simpler)
      try {
        await this.makeRequest<any>('/products', { method: 'GET' });
        return {
          status: 'success',
          message: 'API connection and authentication successful',
          details: { hasToken: true, tokenLength: token.length }
        };
      } catch (authError) {
        return {
          status: 'error', 
          message: `Authenticated request failed: ${authError}`,
          details: { 
            hasToken: true, 
            tokenLength: token.length,
            authError: String(authError)
          }
        };
      }
      
    } catch (error) {
      return {
        status: 'error',
        message: error instanceof Error ? error.message : 'Connection test failed',
        details: { error: String(error) }
      };
    }
  }
}

/**
 * Secure token provider using httpOnly cookies
 */
const tokenStorage: SecureTokenStorage = createTokenStorage(config.apiBaseUrl);

/**
 * Get Access Group authentication token securely
 */
const getAccessGroupToken = async (): Promise<string | null> => {
  try {
    const token = await tokenStorage.getToken();
    if (!token) {
      console.warn('No Access Group token found. User needs to authenticate.');
      return null;
    }
    return token;
  } catch (error) {
    console.error('Error getting authentication token:', error);
    return null;
  }
};

// Export singleton instance
export const feedbackApi = new FeedbackApiClient(config.apiBaseUrl, getAccessGroupToken);

// Export types and classes for external use
export { FeedbackApiClient };
